import React, { useEffect, useRef, useState, forwardRef, useMemo } from 'react';
import { useGLTF } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { Mesh, Vector3, Euler, AnimationMixer, AnimationClip, AnimationAction, Color, Box3 } from 'three';
import * as THREE from 'three';
import { mmlCollisionSystem } from '../utils/MMLCollisionSystem';

interface RobotProps {
  position?: [number, number, number]
  rotation?: [number, number, number]
  onPositionChange?: (position: { x: number, y: number, z: number }, rotation: { x: number, y: number, z: number }) => void
  isRemotePlayer?: boolean // Indica se é um robô de outro jogador
  ref?: React.Ref<Mesh> // Permite passar uma ref externa
  otherPlayers?: { position: Vector3 }[] // Lista de outros jogadores para verificar colisões
  playerId?: string // ID do jogador para identificar o robô
}

export const Robot = forwardRef<Mesh, RobotProps>(({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  onPositionChange,
  isRemotePlayer = false,
  otherPlayers = [],
  playerId = 'local'
}, ref) => {
  const { scene: gltfScene, animations } = useGLTF('/models/gltf/RobotExpressive/RobotExpressive.glb') as any;

  // Definir as dimensões da caixa de colisão do robô
  const robotCollisionBox = useMemo(() => {
    return {
      width: 2.0,   // Largura da caixa de colisão (reduzida para melhor precisão)
      height: 3.0, // Altura da caixa de colisão aumentada para garantir detecção de colisão com o castelo
      depth: 2.0,   // Profundidade da caixa de colisão (reduzida para melhor precisão)
      offsetY: 6.0  // Offset vertical aumentado para alinhar com o centro da caixa mais alta
    };
  }, []);

  // Criar a caixa de colisão do robô
  const robotBoundingBox = useMemo(() => new THREE.Box3(), []);

  // Função para atualizar a caixa de colisão com base na posição atual do robô
  const updateRobotBoundingBox = (position: Vector3) => {
    const halfWidth = robotCollisionBox.width / 2;
    const halfDepth = robotCollisionBox.depth / 2;

    robotBoundingBox.min.set(
      position.x - halfWidth,
      position.y, // Base da caixa no chão
      position.z - halfDepth
    );

    robotBoundingBox.max.set(
      position.x + halfWidth,
      position.y + robotCollisionBox.height,
      position.z + halfDepth
    );
  };

  // Função para verificar colisão com parcels usando a caixa de colisão
  // Retorna um objeto com informações detalhadas sobre a colisão
  const checkParcelCollision = (position: Vector3, previousPosition: Vector3): {
    collision: boolean,
    collisionNormal?: Vector3,
    collisionDepth?: number,
    barrier?: any
  } => {
    // Atualizar a caixa de colisão com a posição atual
    updateRobotBoundingBox(position);

    // Margem reduzida para permitir movimento mais próximo das parcels
    const margin = 0.05;
    const expandedBox = robotBoundingBox.clone().expandByScalar(margin);

    const barriers = mmlCollisionSystem.getBarriers();
    for (const barrier of barriers) {
      // Criar uma caixa de colisão para a parcel
      const parcelBox = new THREE.Box3(
        new THREE.Vector3(
          barrier.position.x,
          barrier.position.y,
          barrier.position.z
        ),
        new THREE.Vector3(
          barrier.position.x + barrier.size.width,
          barrier.position.y + barrier.size.height,
          barrier.position.z + barrier.size.depth
        )
      );

      // Verificar interseção entre as caixas de colisão
      if (expandedBox.intersectsBox(parcelBox)) {
        // Verificar se é uma barreira do castelo
        if (barrier.id.includes('fantasy-castle')) {
          // Calcular a normal da colisão (direção para afastar do castelo)
          const robotCenter = new THREE.Vector3(
            (robotBoundingBox.min.x + robotBoundingBox.max.x) / 2,
            (robotBoundingBox.min.y + robotBoundingBox.max.y) / 2,
            (robotBoundingBox.min.z + robotBoundingBox.max.z) / 2
          );

          // Verificar se o robô está acima do castelo
          // Se a base do robô estiver acima do topo do castelo, não considerar como colisão
          const robotBottom = robotBoundingBox.min.y;
          const castleTop = parcelBox.max.y;

          // Aumentar a margem para garantir que o robô seja considerado "acima" do castelo
          // mesmo quando está próximo ao topo
          const aboveMargin = 1.0; // Margem ainda maior para garantir detecção correta

          // Se o robô estiver claramente acima do castelo, não há colisão
          if (robotBottom > castleTop + aboveMargin) {
            // O robô está acima do castelo, não há colisão
            console.log(`Robô está ACIMA do castelo ${barrier.id}, permitindo movimento normal`);
            continue;
          }

          // Se chegou aqui, o robô NÃO está acima do castelo
          console.log(`Robô NÃO está acima do castelo ${barrier.id}: robotBottom=${robotBottom.toFixed(2)}, castleTop=${castleTop.toFixed(2)}`);

          // Para o castelo, sempre considerar como colisão se não estiver claramente acima
          // Esta é uma abordagem mais conservadora para garantir que o robô nunca transpasse
          if (barrier.id.includes('fantasy-castle')) {
            console.log(`Colisão com castelo detectada: ${barrier.id}`);
          }

          // Encontrar a face mais próxima do castelo para calcular a normal de colisão
          const faces = [
            { normal: new THREE.Vector3(-1, 0, 0), dist: Math.abs(robotCenter.x - parcelBox.min.x), name: 'left' },
            { normal: new THREE.Vector3(1, 0, 0), dist: Math.abs(robotCenter.x - parcelBox.max.x), name: 'right' },
            { normal: new THREE.Vector3(0, -1, 0), dist: Math.abs(robotCenter.y - parcelBox.max.y), name: 'top' },
            { normal: new THREE.Vector3(0, 1, 0), dist: Math.abs(robotCenter.y - parcelBox.min.y), name: 'bottom' },
            { normal: new THREE.Vector3(0, 0, -1), dist: Math.abs(robotCenter.z - parcelBox.min.z), name: 'front' },
            { normal: new THREE.Vector3(0, 0, 1), dist: Math.abs(robotCenter.z - parcelBox.max.z), name: 'back' },
          ];

          // Ordenar as faces pela distância
          faces.sort((a, b) => a.dist - b.dist);

          // Usar a normal da face mais próxima
          const normal = faces[0].normal;

          // Usar uma profundidade de penetração muito maior para garantir que o robô nunca transpasse
          const penetrationDepth = faces[0].dist + 2.0; // Margem de segurança significativamente aumentada

          console.log(`Colisão com o castelo: ${barrier.id}, face: ${faces[0].name}`);
          console.log(`  Posição do robô: ${robotCenter.x.toFixed(2)}, ${robotCenter.y.toFixed(2)}, ${robotCenter.z.toFixed(2)}`);
          console.log(`  Normal de colisão: ${normal.x.toFixed(2)}, ${normal.y.toFixed(2)}, ${normal.z.toFixed(2)}`);
          console.log(`  Profundidade: ${penetrationDepth.toFixed(2)}`);

          return {
            collision: true,
            collisionNormal: normal,
            collisionDepth: penetrationDepth,
            barrier: barrier
          };
        }

        // Para parcelas normais, verificar se está próximo ao topo
        const parcelTop = barrier.position.y + barrier.size.height;
        const robotBottom = robotBoundingBox.min.y;
        const distanceToTop = Math.abs(robotBottom - parcelTop);
        const topMargin = 0.6; // Margem aumentada para facilitar movimento sobre parcels

        // Se a base do robô está próxima ao topo da parcel, não considerar como colisão
        if (distanceToTop <= topMargin && robotBottom >= parcelTop - topMargin) {
          // Ajustar a posição Y para ficar ligeiramente acima da parcel
          // Isso evita o efeito de "grudar" na superfície
          position.y = parcelTop + 0.01; // Pequeno offset para evitar colisões repetidas

          // Sempre permitir ficar sobre a parcel, mesmo parcialmente
          return { collision: false };
        }

        // Calcular a normal da colisão (direção para afastar da parcel)
        // Primeiro, encontrar o ponto mais próximo na superfície da parcel
        const robotCenter = new THREE.Vector3(
          (robotBoundingBox.min.x + robotBoundingBox.max.x) / 2,
          (robotBoundingBox.min.y + robotBoundingBox.max.y) / 2,
          (robotBoundingBox.min.z + robotBoundingBox.max.z) / 2
        );

        const parcelCenter = new THREE.Vector3(
          (parcelBox.min.x + parcelBox.max.x) / 2,
          (parcelBox.min.y + parcelBox.max.y) / 2,
          (parcelBox.min.z + parcelBox.max.z) / 2
        );

        // Calcular a direção da normal (do centro da parcel para o centro do robô)
        const normal = new THREE.Vector3().subVectors(robotCenter, parcelCenter).normalize();

        // Calcular a profundidade da penetração
        // Simplificação: usar a distância entre os centros menos a soma dos raios
        const robotRadius = Math.max(
          (robotBoundingBox.max.x - robotBoundingBox.min.x) / 2,
          (robotBoundingBox.max.z - robotBoundingBox.min.z) / 2
        );

        const parcelRadius = Math.max(
          (parcelBox.max.x - parcelBox.min.x) / 2,
          (parcelBox.max.z - parcelBox.min.z) / 2
        );

        const distance = robotCenter.distanceTo(parcelCenter);
        const penetrationDepth = (robotRadius + parcelRadius) - distance;

        // Caso contrário, há colisão
        return {
          collision: true,
          collisionNormal: normal,
          collisionDepth: penetrationDepth,
          barrier: barrier
        };
      }
    }

    return { collision: false };
  };

  // Função para verificar se o robô está sobre uma parcel
  const isRobotOnGround = (position: Vector3): boolean => {
    // Atualizar a caixa de colisão com a posição atual
    updateRobotBoundingBox(position);

    // Raio para verificação horizontal (um pouco maior que a caixa de colisão)
    const horizontalCheckRadius = Math.max(
      (robotBoundingBox.max.x - robotBoundingBox.min.x) / 2,
      (robotBoundingBox.max.z - robotBoundingBox.min.z) / 2
    ) + 0.1;

    // Ponto central do robô no plano XZ
    const robotCenterX = (robotBoundingBox.min.x + robotBoundingBox.max.x) / 2;
    const robotCenterZ = (robotBoundingBox.min.z + robotBoundingBox.max.z) / 2;
    const robotBottom = robotBoundingBox.min.y;

    const barriers = mmlCollisionSystem.getBarriers();
    for (const barrier of barriers) {
      // Calcular o centro da parcel no plano XZ
      const parcelCenterX = barrier.position.x + barrier.size.width / 2;
      const parcelCenterZ = barrier.position.z + barrier.size.depth / 2;
      const parcelTop = barrier.position.y + barrier.size.height;

      // Calcular a distância horizontal entre o robô e o centro da parcel
      const dx = Math.abs(robotCenterX - parcelCenterX);
      const dz = Math.abs(robotCenterZ - parcelCenterZ);

      // Verificar se o robô está dentro ou próximo dos limites horizontais da parcel
      const isWithinHorizontalRange =
        dx <= (barrier.size.width / 2 + horizontalCheckRadius) &&
        dz <= (barrier.size.depth / 2 + horizontalCheckRadius);

      // Verificar se é uma barreira do castelo
      if (barrier.id.includes('fantasy-castle')) {
        // Verificar se o robô está claramente acima do topo do castelo
        const castleTop = barrier.position.y + barrier.size.height;
        const distanceAboveCastle = robotBottom - castleTop;

        // Se o robô estiver claramente acima do castelo (com uma margem maior),
        // considerar como "estar no chão" para permitir movimento normal
        if (distanceAboveCastle > 0.5 && distanceAboveCastle < 2.0) {
          // O robô está acima do castelo, considerar como "no chão"
          // para permitir movimento normal e evitar aplicação da gravidade
          position.y = castleTop;
          return true;
        }

        // Se não estiver claramente acima, não considerar como "estar no chão"
        // para evitar que o robô fique em cima do castelo em outras situações
        continue;
      }

      if (isWithinHorizontalRange) {
        // Verificar se a base do robô está próxima ao topo da parcel
        const distanceToTop = Math.abs(robotBottom - parcelTop);
        const topMargin = 0.6; // Margem aumentada para movimento mais suave

        if (distanceToTop <= topMargin) {
          // Ajustar a posição Y para ficar ligeiramente acima da parcel
          // Isso evita o efeito de "grudar" na superfície
          position.y = parcelTop + 0.01; // Pequeno offset para evitar colisões repetidas
          return true;
        }
      }
    }

    return false;
  };

  // Declarative clone com metadados e aplicação de cores
  const robotModel = useMemo(() => {
    const clone = gltfScene.clone();

    // Usar a mesma escala do OtherPlayerRobot para manter consistência
    clone.scale.set(0.35, 0.35, 0.35);

    // Armazenar a posição inicial para reposicionamento
    clone.userData.initialPosition = [...position];

    // Código de inspeção detalhado para identificar partes do modelo
    console.log('Iniciando inspeção detalhada do modelo do robô:');
    clone.traverse(function (child: any) {
      // Informações gerais do objeto
      console.log(`Objeto: ${child.name || 'Sem nome'} | Tipo: ${child.type}`);

      // Detalhes para meshes
      if (child.isMesh) {
        console.log(`  Mesh: ${child.name || 'Sem nome'}`);
        if (child.material) {
          console.log(`  Material: ${child.material.name || 'Sem nome'}`);
          console.log(`  Tipo de Material: ${child.material.type}`);
          console.log(`  Tem Textura: ${child.material.map ? 'Sim' : 'Não'}`);
          if (child.material.map) {
            console.log(`  Nome da Textura: ${child.material.map.name || 'Sem nome'}`);
          }
          if (child.material.color) {
            console.log(`  Cor Atual: ${child.material.color.getHexString()}`);
          }
        }

        // Posição para ajudar na identificação
        if (child.position) {
          console.log(`  Posição: X=${child.position.x.toFixed(2)}, Y=${child.position.y.toFixed(2)}, Z=${child.position.z.toFixed(2)}`);
        }
      }

      // Verificar morph targets (caso olhos/sobrancelhas sejam controlados por eles)
      if (child.morphTargetDictionary) {
        console.log(`  Morph Targets em ${child.name || 'Sem nome'}:`, Object.keys(child.morphTargetDictionary));
      }
    });
    console.log('Fim da inspeção do modelo.');

    // Cores do OtherPlayerRobot para manter consistência visual
    const BLACK = '#000000';       // Preto puro
    const DARK_GRAY = '#1A1A1A';   // Cinza escuro (quase preto)
    const MEDIUM_GRAY = '#444444'; // Cinza médio
    const LIGHT_GRAY = '#777777';  // Cinza claro
    const SILVER = '#C0C0C0';      // Prateado
    const CHROME = '#D3D3D3';      // Prateado mais claro (tipo cromo)
    const PLATINUM = '#E5E4E2';    // Platina (prateado brilhante)
    const WHITE = '#FFFFFF';       // Branco
    const GUNMETAL = '#2C3539';    // Metal escuro (tipo arma)

    // Mapeamento de partes para cores específicas - invertido (claro -> escuro, escuro -> claro)
    const partColors = {
      // Cabeça e face
      'head': GUNMETAL,        // Era ALIEN_SILVER (claro), agora escuro
      'face': DARK_GRAY,       // Era SILVER (claro), agora escuro
      'eye': BLACK,            // Era ALIEN_GLOW (claro/brilhante), agora escuro
      'eyebrow': BLACK,        // Alterado para preto para manter consistência
      'mouth': CHROME,         // Era GUNMETAL (escuro), agora claro
      'antenna': BLACK,        // Era ALIEN_GLOW (claro/brilhante), agora escuro

      // Corpo
      'body': GUNMETAL,        // Era SILVER (claro), agora escuro
      'torso': DARK_GRAY,      // Era ALIEN_SILVER (claro), agora escuro
      'chest': DARK_GRAY,      // Era CHROME (claro), agora escuro
      'shoulder': SILVER,      // Era GUNMETAL (escuro), agora claro

      // Braços
      'arm': GUNMETAL,         // Era SILVER (claro), agora escuro
      'hand': CHROME,          // Era GUNMETAL (escuro), agora claro
      'finger': DARK_GRAY,     // Era CHROME (claro), agora escuro
      'elbow': SILVER,         // Era DARK_GRAY (escuro), agora claro
      'wrist': DARK_GRAY,      // Era STEEL_BLUE (médio), agora escuro

      // Pernas
      'leg': GUNMETAL,         // Era SILVER (claro), agora escuro
      'foot': CHROME,          // Era GUNMETAL (escuro), agora claro
      'toe': DARK_GRAY,        // Era CHROME (claro), agora escuro
      'knee': SILVER,          // Era DARK_GRAY (escuro), agora claro
      'ankle': DARK_GRAY,      // Era STEEL_BLUE (médio), agora escuro

      // Detalhes
      'joint': SILVER,         // Era DARK_GRAY (escuro), agora claro
      'button': BLACK,         // Era ALIEN_GLOW (claro/brilhante), agora escuro
      'detail': DARK_GRAY,     // Era STEEL_BLUE (médio), agora escuro
      'accent': BLACK,         // Era ALIEN_GLOW (claro/brilhante), agora escuro
      'panel': CHROME,         // Era GUNMETAL (escuro), agora claro
      'trim': DARK_GRAY,       // Era CHROME (claro), agora escuro
    };

    // Função para determinar a cor com base no nome da parte
    const getColorForPart = (partName: string) => {
      const name = partName.toLowerCase();

      // Verificar correspondências exatas primeiro
      for (const [key, color] of Object.entries(partColors)) {
        if (name === key) {
          return color;
        }
      }

      // Verificar correspondências parciais
      for (const [key, color] of Object.entries(partColors)) {
        if (name.includes(key)) {
          return color;
        }
      }

      // Cores padrão com base em padrões comuns
      if (name.includes('yellow')) return SILVER;     // Era GUNMETAL (escuro), agora claro
      if (name.includes('white')) return DARK_GRAY;   // Era SILVER (claro), agora escuro
      if (name.includes('black')) return WHITE;       // Era BLACK (escuro), agora claro
      if (name.includes('metal')) return GUNMETAL;    // Era ALIEN_SILVER (claro), agora escuro
      if (name.includes('joint')) return SILVER;      // Era DARK_GRAY (escuro), agora claro

      // Padrão para partes não identificadas
      return GUNMETAL;                                // Era SILVER (claro), agora escuro
    };

    // Aplicação do design do OtherPlayerRobot
    // Configurar metadados do robô
    clone.traverse((child: any) => {
      child.userData.robotId = playerId;
      child.userData.isRobotModel = true;
      if (!child.name) child.name = `Robot-${playerId}`;
    });

    // Aplicar cores a todas as partes do modelo
    console.log('Aplicando cores específicas a cada parte do robô...');

    // Rastrear partes já coloridas
    const coloredParts = new Set();

    // Função para aplicar cores a um objeto
    const applyColorToObject = (object: any, color: string) => {
      if (object && object.isMesh && object.material) {
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        materials.forEach(material => {
          material.color.set(color);
          material.needsUpdate = true;
        });
        console.log(`Cor ${color} aplicada a: ${object.name}`);
      }
    };

    // 1. Olhos e sobrancelhas sempre pretos
    clone.traverse((object) => {
      const name = object.name.toLowerCase();
      if (name.includes('eye') || name.includes('eyebrow')) {
        applyColorToObject(object, BLACK);
        coloredParts.add(object.uuid);
        console.log(`Olho/sobrancelha colorido de preto: ${object.name}`);
      }
    });

    // 2. Corpo principal em escuro com detalhes claros (invertido)
    const body = clone.getObjectByName('Body');
    if (body) {
      applyColorToObject(body, GUNMETAL);
      coloredParts.add(body.uuid);
      console.log(`Corpo principal colorido de gunmetal (escuro): ${body.name}`);

      // Adicionar detalhes ao corpo - invertido
      body.traverse((part) => {
        if (part !== body && part.isMesh && !coloredParts.has(part.uuid)) {
          const name = part.name.toLowerCase();
          // Articulações e juntas em prateado (claro)
          if (name.includes('joint') || name.includes('connector')) {
            applyColorToObject(part, SILVER);
            coloredParts.add(part.uuid);
          }
          // Detalhes do peito em cinza escuro
          else if (name.includes('chest') || name.includes('front')) {
            applyColorToObject(part, DARK_GRAY);
            coloredParts.add(part.uuid);
          }
          // Detalhes laterais em cromo (claro)
          else if (name.includes('side') || name.includes('panel')) {
            applyColorToObject(part, CHROME);
            coloredParts.add(part.uuid);
          }
          // Botões ou controles em preto
          else if (name.includes('button') || name.includes('control')) {
            applyColorToObject(part, BLACK);
            coloredParts.add(part.uuid);
          }
        }
      });
    }

    // 3. Braços e pernas com padrão invertido (escuro onde era claro, claro onde era escuro)
    clone.traverse((object) => {
      if (object.isMesh && !coloredParts.has(object.uuid)) {
        const name = object.name.toLowerCase();

        // Braços: base escura, detalhes claros, mãos em cromo
        if (name.includes('arm')) {
          if (name.includes('upper')) {
            applyColorToObject(object, GUNMETAL);       // Era SILVER (claro), agora escuro
          } else if (name.includes('lower')) {
            applyColorToObject(object, DARK_GRAY);       // Era ALIEN_SILVER (claro), agora escuro
          } else if (name.includes('joint') || name.includes('elbow')) {
            applyColorToObject(object, SILVER);         // Era DARK_GRAY (escuro), agora claro
          } else {
            applyColorToObject(object, GUNMETAL);       // Era SILVER (claro), agora escuro
          }
          coloredParts.add(object.uuid);
        }
        else if (name.includes('hand') || name.includes('fist')) {
          applyColorToObject(object, CHROME);           // Era GUNMETAL (escuro), agora claro
          coloredParts.add(object.uuid);
        }
        else if (name.includes('finger') || name.includes('thumb')) {
          applyColorToObject(object, DARK_GRAY);        // Era CHROME (claro), agora escuro
          coloredParts.add(object.uuid);
        }
        else if (name.includes('wrist')) {
          applyColorToObject(object, DARK_GRAY);         // Era STEEL_BLUE (médio), agora escuro
          coloredParts.add(object.uuid);
        }
        // Pernas: base escura, detalhes claros, pés em cromo
        else if (name.includes('leg')) {
          if (name.includes('upper')) {
            applyColorToObject(object, GUNMETAL);       // Era SILVER (claro), agora escuro
          } else if (name.includes('lower')) {
            applyColorToObject(object, DARK_GRAY);       // Era ALIEN_SILVER (claro), agora escuro
          } else if (name.includes('joint') || name.includes('knee')) {
            applyColorToObject(object, SILVER);         // Era DARK_GRAY (escuro), agora claro
          } else {
            applyColorToObject(object, GUNMETAL);       // Era SILVER (claro), agora escuro
          }
          coloredParts.add(object.uuid);
        }
        else if (name.includes('foot') || name.includes('feet')) {
          applyColorToObject(object, CHROME);           // Era GUNMETAL (escuro), agora claro
          coloredParts.add(object.uuid);
        }
        else if (name.includes('toe')) {
          applyColorToObject(object, DARK_GRAY);         // Era CHROME (claro), agora escuro
          coloredParts.add(object.uuid);
        }
        else if (name.includes('ankle')) {
          applyColorToObject(object, DARK_GRAY);         // Era STEEL_BLUE (médio), agora escuro
          coloredParts.add(object.uuid);
        }
      }
    });

    // 4. Partes brancas originais agora em cinza escuro (invertido)
    clone.traverse((object) => {
      if (object.isMesh && !coloredParts.has(object.uuid)) {
        const materials = Array.isArray(object.material) ? object.material : [object.material];

        materials.forEach(material => {
          const color = material.color;
          // Identificar partes brancas pela cor
          if (color.r > 0.8 && color.g > 0.8 && color.b > 0.8) {
            material.color.set(DARK_GRAY);                // Era SILVER (claro), agora escuro
            material.needsUpdate = true;
            coloredParts.add(object.uuid);
            console.log(`Parte branca alterada para cinza escuro: ${object.name}`);
          }
        });
      }
    });

    // 5. Partes amarelas originais agora em prateado (invertido)
    clone.traverse((object) => {
      if (object.isMesh && !coloredParts.has(object.uuid)) {
        const materials = Array.isArray(object.material) ? object.material : [object.material];

        materials.forEach(material => {
          const color = material.color;
          // Identificar partes amarelas pela cor
          if (color.r > 0.8 && color.g > 0.8 && color.b < 0.3) {
            material.color.set(SILVER);                   // Era GUNMETAL (escuro), agora claro
            material.needsUpdate = true;
            coloredParts.add(object.uuid);
            console.log(`Parte amarela alterada para prateado: ${object.name}`);
          }
        });
      }
    });

    // 6. Colorir partes restantes com base no nome
    clone.traverse((object) => {
      if (object.isMesh && !coloredParts.has(object.uuid)) {
        const color = getColorForPart(object.name);
        applyColorToObject(object, color);
        coloredParts.add(object.uuid);
        console.log(`Parte restante colorida com base no nome: ${object.name} -> ${color}`);
      }
    });

    // 7. Adicionar detalhes finais para garantir um visual interessante - invertido
    // Garantir que a cabeça tenha detalhes metálicos e alienígenas
    const head = clone.getObjectByName('Head');
    if (head) {
      // Manter a cabeça em gunmetal (escuro)
      applyColorToObject(head, GUNMETAL);                // Era ALIEN_SILVER (claro), agora escuro

      // Adicionar detalhes à cabeça - invertido
      head.traverse((part) => {
        if (part !== head && part.isMesh) {
          const name = part.name.toLowerCase();
          // Olhos em preto
          if (name.includes('eye')) {
            applyColorToObject(part, BLACK);              // Era ALIEN_GLOW (claro), agora escuro
          }
          // Sobrancelhas também em preto
          else if (name.includes('eyebrow')) {
            applyColorToObject(part, BLACK);             // Alterado para preto para manter consistência
          }
          // Antenas ou detalhes superiores em preto
          else if (name.includes('antenna') || name.includes('top')) {
            applyColorToObject(part, BLACK);            // Era ALIEN_GLOW (claro), agora escuro
          }
          // Boca ou detalhes faciais em cromo (claro)
          else if (name.includes('mouth') || name.includes('face')) {
            applyColorToObject(part, CHROME);           // Era GUNMETAL (escuro), agora claro
          }
          // Detalhes laterais da cabeça em cinza escuro
          else if (name.includes('side') || name.includes('ear')) {
            applyColorToObject(part, DARK_GRAY);        // Era STEEL_BLUE (médio), agora escuro
          }
          // Detalhes traseiros da cabeça em cromo (claro)
          else if (name.includes('back') || name.includes('rear')) {
            applyColorToObject(part, CHROME);           // Era GUNMETAL (escuro), agora claro
          }
          // Outros detalhes em cinza escuro
          else if (name.includes('detail') || name.includes('accent')) {
            applyColorToObject(part, DARK_GRAY);        // Era CHROME (claro), agora escuro
          }
        }
      });
    }

    // 8. Adicionar detalhes metálicos aleatórios para criar uma textura mais interessante - invertido
    // Selecionar algumas partes aleatórias para adicionar detalhes metálicos
    clone.traverse((object) => {
      if (object.isMesh && !coloredParts.has(object.uuid)) {
        // Aplicar cores metálicas a algumas partes restantes para criar textura - invertido
        const randomValue = Math.random();
        if (randomValue < 0.2) {
          applyColorToObject(object, SILVER);            // Era GUNMETAL (escuro), agora claro
          console.log(`Detalhe aleatório em prateado: ${object.name}`);
        } else if (randomValue < 0.4) {
          applyColorToObject(object, GUNMETAL);          // Era ALIEN_SILVER (claro), agora escuro
          console.log(`Detalhe aleatório em gunmetal: ${object.name}`);
        } else if (randomValue < 0.5) {
          applyColorToObject(object, DARK_GRAY);         // Era CHROME (claro), agora escuro
          console.log(`Detalhe aleatório em cinza escuro: ${object.name}`);
        } else if (randomValue < 0.55) {
          applyColorToObject(object, BLACK);             // Era ALIEN_GLOW (claro), agora escuro
          console.log(`Detalhe aleatório em preto: ${object.name}`);
        } else {
          applyColorToObject(object, GUNMETAL);         // Era SILVER (claro), agora escuro
          console.log(`Detalhe aleatório em gunmetal: ${object.name}`);
        }
        coloredParts.add(object.uuid);
      }
    });

    return clone;
  }, [gltfScene, playerId]);

  // Mixer e actions
  const mixer = useMemo(() => new AnimationMixer(robotModel), [robotModel]);
  const actions = useMemo(() => {
    const map: Record<string, AnimationAction> = {};
    animations.forEach((clip: AnimationClip) => {
      map[clip.name] = mixer.clipAction(clip);
    });
    return map;
  }, [animations, mixer]);

  // Rodar Idle
  useEffect(() => {
    actions['Idle']?.play();
    return () => mixer.stopAllAction();
  }, [actions, mixer]);

  // Atualizar mixer a cada frame
  useFrame((_, delta) => mixer.update(delta));

  // Obter a câmera do Three.js
  const { camera } = useThree();

  // Referências para estado de movimento e ângulos da câmera
  const moveState = useRef({
    forward: false,
    backward: false,
    left: false,
    right: false,
    up: false,
    down: false,
    isShiftPressed: false,
    speed: 0.5,
    cameraAngleX: 0,
    cameraAngleY: Math.PI / 4,
    cameraDistance: 30,
    targetCameraDistance: 30, // Alvo para smooth zoom
    zoomVelocity: 0, // Velocidade atual do zoom para inércia
    lastMouseX: 0,
    lastMouseY: 0,
    isDragging: false,
    minZoom: 2, // Reduzido ainda mais para permitir zoom extremamente próximo
    maxZoom: 200, // Aumentado significativamente para permitir zoom muito mais distante
    zoomSmoothingFactor: 0.15, // Fator de suavização para o zoom (0-1) - aumentado para zoom mais responsivo
    zoomInertia: 0.85, // Fator de inércia para o zoom (0-1) - reduzido para menor inércia e resposta mais rápida
    currentState: 'Idle',
    isEmoting: false,
    isMouseDown: false,
    isJumping: false, // Flag para controlar o estado de pulo
    jumpVelocity: new Vector3(0, 0, 0), // Velocidade horizontal do pulo
    jumpVerticalVelocity: 0, // Velocidade vertical do pulo (para trajetória parabólica)
    jumpStartTime: 0, // Tempo de início do pulo
    jumpStartPosition: new Vector3(), // Posição inicial do pulo
    jumpGravity: 0.05, // Gravidade aumentada para reduzir a distância do pulo pela metade
    isOnGround: false, // Novo campo para rastrear se o robô está no chão
    // Novo estado para rastrear explicitamente o movimento direcional durante o pulo
    jumpDirectional: {
      active: false,           // Se há movimento direcional ativo durante o pulo
      direction: new Vector3(), // Direção normalizada do movimento
      speed: 0,                // Magnitude da velocidade
      timestamp: 0             // Quando o estado foi capturado
    },
    // Direção atual do movimento para determinar a rotação
    movementDirection: new Vector3(0, 0, 0)
  });

  // Verificar se o componente deve ser renderizado com base no status do Multiverso Pass
  const [shouldRender, setShouldRender] = useState(true);

  // Animation and movement - implementação completamente reescrita para corrigir as direções
  useFrame((state, delta) => {
    if (robotModel && !isRemotePlayer) {
      // Inicialização da posição
      if (!robotModel.userData.initialized) {
        robotModel.position.set(...position);
        robotModel.userData.initialized = true;
      }

      // Atualizar o mixer de animação
      mixer.update(delta);

      // NOVA ABORDAGEM: Configurando velocidades diretamente sem cálculos
      // Velocidade normal e com Shift extremamente diferentes para garantir diferença visível
      // Velocidades reduzidas conforme solicitado, mas mantendo fluidez
      const normalSpeed = 0.8; // Velocidade normal reduzida
      const runningSpeed = 1.8; // Velocidade de corrida reduzida, mas mantendo diferença

      // Define a velocidade base diretamente sem multiplicações
      const baseSpeed = moveState.current.isShiftPressed ? runningSpeed : normalSpeed;
      // Pular normalização por delta para garantir valores consistentes
      const currentSpeed = baseSpeed;

      // Movimento com vetores para maior fluidez
      const moveVector = new Vector3();

      // Velocidades ajustadas para movimento mais lento, mas ainda fluído
      // Movimento para frente/trás (eixo Z) com intensidade reduzida
      if (moveState.current.forward) moveVector.z -= currentSpeed * 1.1; // Velocidade reduzida, mas ainda maior para frente
      if (moveState.current.backward) moveVector.z += currentSpeed * 0.9; // Velocidade reduzida para trás

      // Movimento lateral (eixo X) - velocidade reduzida mas mantendo equilíbrio
      if (moveState.current.left) moveVector.x -= currentSpeed * 0.9;
      if (moveState.current.right) moveVector.x += currentSpeed * 0.9;

      // Movimento vertical (eixo Y)
      if (moveState.current.down) moveVector.y -= currentSpeed;

      // Salvar a posição anterior para verificação de colisão
      const previousPosition = robotModel.position.clone();

      // Sistema de gravidade - verificar se o robô está sobre uma parcel
      const gravityVector = new Vector3(0, -0.2, 0); // Gravidade um pouco mais forte para queda mais rápida e natural

      // Verificar se o robô está sobre uma parcel usando a caixa de colisão
      // e armazenar o resultado para uso posterior
      const isOnGround = isRobotOnGround(robotModel.position);

      // Detectando transição do ar para o chão (importante para continuar o movimento após o pulo)
      const wasInAir = !moveState.current.isOnGround;
      const justLanded = wasInAir && isOnGround;

      // Armazenar o estado atual (no chão ou não) para referência futura
      moveState.current.isOnGround = isOnGround;

      // Se acabou de tocar o chão e ainda tem input direcional, adicionar um impulso
      // na direção do movimento para garantir continuidade
      if (justLanded && (moveState.current.forward || moveState.current.backward ||
                          moveState.current.left || moveState.current.right)) {
        const boostSpeed = moveState.current.isShiftPressed ? 1.0 : 0.7;
        if (moveState.current.forward) moveVector.z -= boostSpeed;
        if (moveState.current.backward) moveVector.z += boostSpeed;
        if (moveState.current.left) moveVector.x -= boostSpeed;
        if (moveState.current.right) moveVector.x += boostSpeed;
      }

      // Sistema de pulo físico com trajetória parabólica
      if (moveState.current.isJumping) {
        // Aplicar a física do pulo
        // 1. Aplicar a gravidade à velocidade vertical
        moveState.current.jumpVerticalVelocity -= moveState.current.jumpGravity;

        // 2. Aplicar a velocidade vertical ao movimento (componente vertical do pulo)
        moveVector.y += moveState.current.jumpVerticalVelocity;

        // 3. REDESENHO COMPLETO: Sistema de prioridade absoluta para movimento direcional
        // O movimento direcional (WASD) tem prioridade absoluta sobre QUALQUER outro sistema

        // Verificar se há teclas de direção pressionadas (prioridade máxima)
        const hasDirectionalInput = moveState.current.forward || moveState.current.backward ||
                                   moveState.current.left || moveState.current.right;

        // Determinar o movimento direcional atual com base no input do usuário
        const directionalVector = new Vector3();
        if (moveState.current.forward) directionalVector.z -= 1;
        if (moveState.current.backward) directionalVector.z += 1;
        if (moveState.current.left) directionalVector.x -= 1;
        if (moveState.current.right) directionalVector.x += 1;

        // IMPORTANTE: Se há input direcional, aplicar com prioridade absoluta
        if (directionalVector.length() > 0) {
          // Normalizar e aplicar rotação da câmera
          directionalVector.normalize();
          directionalVector.applyAxisAngle(new Vector3(0, 1, 0), moveState.current.cameraAngleX);

          // Calcular a velocidade base com base no estado de shift
          const baseSpeed = moveState.current.isShiftPressed ? 1.8 : 1.5;

          // CRÍTICO: Substituir COMPLETAMENTE o movimento horizontal pelo input atual
          // Isso garante que o controle direcional tenha prioridade absoluta
          moveVector.x = directionalVector.x * baseSpeed;
          moveVector.z = directionalVector.z * baseSpeed;

          // Atualizar o estado direcional para referência
          moveState.current.jumpDirectional = {
            active: true,
            direction: directionalVector.clone(),
            speed: baseSpeed,
            timestamp: performance.now()
          };

          if (process.env.NODE_ENV === 'development') {
            console.log("Movimento direcional aplicado durante o pulo:", {
              direction: directionalVector.clone(),
              speed: baseSpeed
            });
          }
        }
        // Se não há input atual mas havia anteriormente, preservar esse movimento
        else if (moveState.current.jumpDirectional.active) {
          // Usar a direção capturada anteriormente, com uma pequena desaceleração
          const preservedSpeed = moveState.current.jumpDirectional.speed * 0.98; // Desaceleração mais suave
          moveVector.x = moveState.current.jumpDirectional.direction.x * preservedSpeed;
          moveVector.z = moveState.current.jumpDirectional.direction.z * preservedSpeed;

          // Atualizar a velocidade preservada
          moveState.current.jumpDirectional.speed = preservedSpeed;

          if (process.env.NODE_ENV === 'development') {
            console.log("Preservando movimento anterior durante o pulo");
          }
        }
        // Caso extremamente improvável: não há input e jumpDirectional não está ativo
        // Isso não deveria acontecer com nossa nova implementação, mas por segurança:
        else {
          // Usar a direção da câmera como fallback
          const fallbackDirection = new Vector3(0, 0, -1);
          fallbackDirection.applyAxisAngle(new Vector3(0, 1, 0), moveState.current.cameraAngleX);

          const fallbackSpeed = moveState.current.isShiftPressed ? 1.5 : 1.2;
          moveVector.x = fallbackDirection.x * fallbackSpeed;
          moveVector.z = fallbackDirection.z * fallbackSpeed;

          // Atualizar o estado direcional
          moveState.current.jumpDirectional = {
            active: true,
            direction: fallbackDirection.clone(),
            speed: fallbackSpeed,
            timestamp: performance.now()
          };

          console.warn("Fallback de direção aplicado durante o pulo - situação inesperada");
        }

        // Aplicar um boost reduzido para compensar a resistência do ar
        // Reduzido para diminuir a distância do pulo pela metade
        const jumpBoost = 1.1;
        moveVector.x *= jumpBoost;
        moveVector.z *= jumpBoost;

        // Não precisamos mais atualizar a velocidade do pulo separadamente
        // pois estamos usando diretamente o input do usuário

        // 4. Verificar se o robô atingiu o chão após o pulo
        if (isOnGround && moveState.current.jumpVerticalVelocity < 0) {
          // Terminar o pulo quando o robô atinge o chão com velocidade negativa (caindo)
          moveState.current.isJumping = false;

          // Zerar apenas a velocidade vertical, mantendo o movimento horizontal
          moveState.current.jumpVerticalVelocity = 0;

          // Verificar se há teclas de direção pressionadas
          const hasHorizontalInput = moveState.current.forward || moveState.current.backward ||
                                     moveState.current.left || moveState.current.right;

          // REDESENHO COMPLETO: Sistema de continuidade pós-pulo
          // Garantir que o movimento continue fluido após o pulo se as teclas ainda estiverem pressionadas
          if (hasHorizontalInput) {
            // Criar um vetor baseado no input do usuário
            const moveDirection = new Vector3();
            if (moveState.current.forward) moveDirection.z -= 1;
            if (moveState.current.backward) moveDirection.z += 1;
            if (moveState.current.left) moveDirection.x -= 1;
            if (moveState.current.right) moveDirection.x += 1;

            if (moveDirection.length() > 0) {
              moveDirection.normalize();
              // Aplicar rotação da câmera ao movimento
              moveDirection.applyAxisAngle(new Vector3(0, 1, 0), moveState.current.cameraAngleX);

              // IMPORTANTE: Substituir completamente o movimento horizontal pelo input atual
              // em vez de apenas adicionar um impulso
              const baseSpeed = moveState.current.isShiftPressed ? 1.8 : 1.5;
              moveVector.x = moveDirection.x * baseSpeed;
              moveVector.z = moveDirection.z * baseSpeed;

              // Adicionar um impulso extra para garantir continuidade do movimento
              const continuityBoost = moveState.current.isShiftPressed ? 1.5 : 1.2;
              moveVector.x += moveDirection.x * continuityBoost;
              moveVector.z += moveDirection.z * continuityBoost;

              if (process.env.NODE_ENV === 'development') {
                console.log("Continuidade de movimento aplicada após o pulo");
              }
            }
          }

          // Restaurar a animação apropriada com base nas teclas pressionadas
          if (!hasHorizontalInput) {
            // Se não houver input, voltar para Idle
            actions['Idle']?.reset();
            actions['Idle']?.play();
            moveState.current.currentState = 'Idle';
          } else {
            // Se houver input, continuar com Walking ou Running
            // dependendo se Shift está pressionado
            const nextAnim = moveState.current.isShiftPressed ? 'Running' : 'Walking';
            actions[nextAnim]?.reset();
            actions[nextAnim]?.play();
            moveState.current.currentState = nextAnim;
          }
        }
      }
      // Aplicar gravidade se não estiver no chão e não estiver pulando
      else if (!isOnGround) {
        moveVector.add(gravityVector);
      }

      // Verificar se houve movimento
      const moved = moveVector.length() > 0;

      // Forçar mudança de animação com base na velocidade atual
      if (moved && !moveState.current.isJumping) { // Não mudar animação durante o pulo
        if (moveState.current.isShiftPressed) {
          // Quando shift está pressionado, sempre usar Running
          if (moveState.current.currentState !== 'Running') {
            moveState.current.currentState = 'Running';
            actions['Running']?.reset();
            actions['Running']?.play();
          }
        } else {
          // Caso contrário, usar Walking
          if (moveState.current.currentState !== 'Walking' && !moveState.current.isEmoting) {
            moveState.current.currentState = 'Walking';
            actions['Walking']?.reset();
            actions['Walking']?.play();
          }
        }
      }

      // Salvar a direção do movimento para uso na rotação
      if (moved) {
        moveState.current.movementDirection = moveVector.clone().normalize();
      }

      // Aplicar movimento relativo à direção da câmera
      if (moved || moveState.current.isJumping) { // Aplicar movimento mesmo durante o pulo
        // Aplicar rotação da câmera ao movimento
        const rotatedMove = moveVector.clone();
        rotatedMove.applyAxisAngle(new Vector3(0, 1, 0), moveState.current.cameraAngleX);

        // REDESENHO COMPLETO: Sistema de prioridade absoluta para movimento direcional
        // Garantir que o movimento direcional seja SEMPRE respeitado durante o pulo
        if (moveState.current.isJumping) {
          // Verificar se há teclas de direção pressionadas (prioridade máxima)
          const hasDirectionalInput = moveState.current.forward || moveState.current.backward ||
                                     moveState.current.left || moveState.current.right;

          // Se há input direcional, substituir completamente o movimento horizontal
          if (hasDirectionalInput) {
            // Criar um vetor baseado no input do usuário
            const moveDirection = new Vector3();
            if (moveState.current.forward) moveDirection.z -= 1;
            if (moveState.current.backward) moveDirection.z += 1;
            if (moveState.current.left) moveDirection.x -= 1;
            if (moveState.current.right) moveDirection.x += 1;

            if (moveDirection.length() > 0) {
              moveDirection.normalize();
              // Aplicar rotação da câmera ao movimento
              moveDirection.applyAxisAngle(new Vector3(0, 1, 0), moveState.current.cameraAngleX);

              // Calcular a velocidade com base no estado de shift
              const speed = moveState.current.isShiftPressed ? 1.8 : 1.5;

              // Extrair apenas o componente vertical do movimento atual
              const verticalComponent = rotatedMove.y;

              // CRÍTICO: Substituir COMPLETAMENTE o movimento horizontal pelo input atual
              // Isso garante que o controle direcional tenha prioridade absoluta
              rotatedMove.x = moveDirection.x * speed;
              rotatedMove.z = moveDirection.z * speed;

              // Restaurar o componente vertical
              rotatedMove.y = verticalComponent;

              // Adicionar um boost adicional para garantir máxima responsividade
              const boostFactor = moveState.current.isShiftPressed ? 0.5 : 0.4;
              rotatedMove.x *= (1 + boostFactor);
              rotatedMove.z *= (1 + boostFactor);

              // Atualizar o estado direcional
              moveState.current.jumpDirectional = {
                active: true,
                direction: moveDirection.clone(),
                speed: speed,
                timestamp: performance.now()
              };

              if (process.env.NODE_ENV === 'development') {
                console.log("Movimento direcional aplicado com prioridade absoluta");
              }
            }
          }
          // Se não há input atual mas jumpDirectional está ativo, verificar e corrigir
          else if (moveState.current.jumpDirectional.active) {
            // Extrair apenas o componente horizontal do movimento
            const horizontalMove = new Vector3(rotatedMove.x, 0, rotatedMove.z);
            const horizontalLength = horizontalMove.length();

            // Verificar se há um movimento horizontal significativo
            if (horizontalLength > 0.1) {
              // Normalizar para comparar apenas a direção
              const currentDirection = horizontalMove.clone().normalize();
              const expectedDirection = moveState.current.jumpDirectional.direction.clone();

              // Calcular o ângulo entre o movimento atual e a direção esperada
              const dotProduct = currentDirection.dot(expectedDirection);
              // Clamp para evitar erros de precisão numérica
              const angleDeviation = Math.acos(Math.min(1, Math.max(-1, dotProduct)));

              // Se o desvio for significativo (mais de ~10 graus), corrigir
              if (angleDeviation > 0.17) {
                if (process.env.NODE_ENV === 'development') {
                  console.warn("Corrigindo desvio de direção durante o pulo:",
                    (angleDeviation * 180 / Math.PI).toFixed(2) + "°");
                }

                // Extrair o componente vertical
                const verticalComponent = rotatedMove.y;

                // Recalcular o movimento para alinhar com a direção esperada
                rotatedMove.x = expectedDirection.x * horizontalLength;
                rotatedMove.z = expectedDirection.z * horizontalLength;

                // Restaurar o componente vertical
                rotatedMove.y = verticalComponent;
              }
            }
          }
        }

        // Não precisamos mais do código para velocidade residual do pulo
        // pois estamos usando diretamente o input do usuário para o movimento

        // Sistema de desvio físico avançado para o robô
        // Implementa um sistema de deslizamento natural ao longo das superfícies

        // Primeiro, verificar se o movimento completo causaria colisão
        const targetPosition = robotModel.position.clone().add(rotatedMove);
        const collisionResult = checkParcelCollision(targetPosition, previousPosition);

        if (!collisionResult.collision) {
          // Se não houver colisão, aplicar o movimento normalmente
          robotModel.position.add(rotatedMove);
        } else {
          // Se houver colisão, implementar o sistema de deslizamento

          // 1. Obter a normal da colisão e a profundidade
          const normal = collisionResult.collisionNormal || new Vector3(0, 1, 0); // Valor padrão se indefinido
          const depth = collisionResult.collisionDepth || 0.1;

          // 2. Calcular o vetor de deslizamento ao longo da superfície
          // Projetar o movimento na direção tangente à superfície
          const dot = rotatedMove.dot(normal);
          const projection = normal.clone().multiplyScalar(dot);

          // Vetor de deslizamento = movimento original - projeção na normal
          let slideVector = rotatedMove.clone().sub(projection);

          // REDESENHO COMPLETO: Sistema de preservação de movimento durante colisões
          // Garantir que o movimento direcional seja preservado mesmo durante colisões

          // Verificar se há input direcional atual (prioridade absoluta)
          const hasDirectionalInput = moveState.current.forward || moveState.current.backward ||
                                     moveState.current.left || moveState.current.right;

          if (hasDirectionalInput) {
            // Criar um vetor baseado no input do usuário
            const moveDirection = new Vector3();
            if (moveState.current.forward) moveDirection.z -= 1;
            if (moveState.current.backward) moveDirection.z += 1;
            if (moveState.current.left) moveDirection.x -= 1;
            if (moveState.current.right) moveDirection.x += 1;

            if (moveDirection.length() > 0) {
              moveDirection.normalize();
              // Aplicar rotação da câmera ao movimento
              moveDirection.applyAxisAngle(new Vector3(0, 1, 0), moveState.current.cameraAngleX);

              // Projetar o vetor direcional na superfície de colisão
              const dirDot = moveDirection.dot(normal);
              const dirProjection = normal.clone().multiplyScalar(dirDot);
              const tangentialDir = moveDirection.clone().sub(dirProjection).normalize();

              // Calcular a velocidade do movimento tangencial
              const speed = moveState.current.isShiftPressed ? 1.8 : 1.5;

              // IMPORTANTE: Substituir completamente o vetor de deslizamento
              // Isso garante que o movimento direcional tenha prioridade absoluta
              slideVector = tangentialDir.multiplyScalar(speed * 1.2);

              if (process.env.NODE_ENV === 'development') {
                console.log("Movimento direcional preservado durante colisão");
              }
            }
          }
          // Se não há input atual mas estamos pulando, preservar a direção do pulo
          else if (moveState.current.isJumping && moveState.current.jumpDirectional.active) {
            // Obter a direção do movimento direcional durante o pulo
            const jumpDir = moveState.current.jumpDirectional.direction.clone();

            // Projetar a direção do pulo na superfície de colisão
            const jumpDirDot = jumpDir.dot(normal);
            const jumpDirProjection = normal.clone().multiplyScalar(jumpDirDot);
            const tangentialJumpDir = jumpDir.clone().sub(jumpDirProjection).normalize();

            // Calcular o vetor de deslizamento preservando a direção original do pulo
            const slideSpeed = moveState.current.jumpDirectional.speed * 0.8;
            const jumpSlideVector = tangentialJumpDir.multiplyScalar(slideSpeed);

            // Combinar com o vetor de deslizamento atual
            slideVector.add(jumpSlideVector);

            if (process.env.NODE_ENV === 'development') {
              console.log("Preservando direção do pulo durante colisão");
            }
          }

          // O código anterior foi substituído pelo sistema de preservação de movimento
          // que já lida com o input direcional e a preservação do movimento durante colisões

          // Verificar se a colisão é com o castelo
          const isCastleCollision = collisionResult.barrier && collisionResult.barrier.id.includes('fantasy-castle');

          // REDESENHO: Sistema de movimento suave sobre parcels
          // Verificar se estamos colidindo com uma parcel normal (não castelo)
          const isNormalParcel = !isCastleCollision;

          // Verificar se estamos tentando subir em uma parcel (movimento para cima)
          const isMovingUp = rotatedMove.y > 0;

          // Verificar se estamos no topo ou próximos ao topo da parcel
          let isNearTop = false;
          if (collisionResult.barrier) {
            const parcelTop = collisionResult.barrier.position.y + collisionResult.barrier.size.height;
            const robotBottom = robotBoundingBox.min.y;
            const distanceToTop = Math.abs(robotBottom - parcelTop);
            isNearTop = distanceToTop < 0.8; // Margem maior para detecção de topo
          }

          // Adicionar um componente vertical para "subir" sobre obstáculos
          // Quanto mais perpendicular o movimento for à superfície, maior o componente vertical
          const perpendicularFactor = Math.abs(dot) / rotatedMove.length();

          // Componente vertical aumentado para parcels normais
          // Para o castelo, não permitir subir (componente vertical zero)
          const upwardComponent = isCastleCollision ? 0 : Math.min(perpendicularFactor * 2.0, 1.0);

          // Fator de deslizamento aumentado para parcels normais
          // Para o castelo, não permitir deslizamento (fator zero)
          const slideFactor = isCastleCollision ? 0 : 1.5;

          // Se estamos próximos ao topo de uma parcel normal, dar um impulso extra para subir
          const topBoost = (isNormalParcel && isNearTop) ? 0.5 : 0.0;

          if (process.env.NODE_ENV === 'development') {
            console.log(`Aplicando deslizamento com fator: ${slideFactor} (castelo: ${isCastleCollision})`);
          }

          // Criar o vetor de movimento final com deslizamento e componente vertical
          const slideMove = new Vector3(
            slideVector.x * slideFactor,      // Ajustar o deslizamento horizontal com base no tipo de colisão
            upwardComponent + topBoost,       // Componente vertical para subir + impulso extra para topos
            slideVector.z * slideFactor       // Ajustar o deslizamento horizontal com base no tipo de colisão
          );

          // 3. Verificar se o movimento de deslizamento causa colisão
          const slidePosition = robotModel.position.clone().add(slideMove);
          const slideCollisionResult = checkParcelCollision(slidePosition, previousPosition);

          if (!slideCollisionResult.collision) {
            // Se o deslizamento não causa colisão, aplicá-lo
            robotModel.position.add(slideMove);
          } else {
            // 4. Se o deslizamento ainda causa colisão, tentar apenas o componente vertical
            // Aumentar significativamente o componente vertical para facilitar subir em parcels
            const upwardMove = new Vector3(0, (upwardComponent + topBoost) * 2.0, 0);
            const upwardPosition = robotModel.position.clone().add(upwardMove);
            const upwardCollisionResult = checkParcelCollision(upwardPosition, previousPosition);

            if (!upwardCollisionResult.collision) {
              // Se o movimento vertical não causa colisão, aplicá-lo
              robotModel.position.add(upwardMove);
            } else {
              // 5. Se tudo falhar, tentar um movimento na direção oposta à colisão
              // Verificar se a colisão é com o castelo para aplicar uma força maior
              const isCastleCollision = slideCollisionResult.barrier && slideCollisionResult.barrier.id.includes('fantasy-castle');

              console.log(`Aplicando escape para colisão com: ${isCastleCollision ? 'castelo' : 'outro objeto'}`);

              // Se for colisão com o castelo, garantir que o robô NUNCA transpasse
              if (isCastleCollision) {
                // SEMPRE voltar à posição anterior para garantir que o robô nunca transpasse o castelo
                // Esta é a abordagem mais segura e confiável
                robotModel.position.copy(previousPosition);

                // Adicionar um pequeno deslocamento na direção oposta à colisão para evitar ficar preso
                // Usar uma força maior para garantir que o robô seja empurrado para longe do castelo
                const pushFactor = 0.5; // Fator de empurrão significativo
                const pushDirection = normal.clone().multiplyScalar(pushFactor);
                robotModel.position.add(pushDirection);

                console.log(`Colisão com castelo: posição restaurada e aplicado empurrão de segurança`);

                // Verificar se o robô está tentando subir (movimento vertical positivo)
                const isMovingUp = rotatedMove.y > 0;

                // Verificar se o robô está próximo ao topo do castelo
                const robotBottom = robotBoundingBox.min.y;
                const castleTop = slideCollisionResult.barrier.position.y + slideCollisionResult.barrier.size.height;
                const distanceToTop = Math.abs(robotBottom - castleTop);

                // Se estiver tentando subir e estiver próximo ao topo, dar um impulso extra
                if (isMovingUp && distanceToTop < 1.0) {
                  const upwardBoost = new Vector3(0, 1.5, 0); // Impulso vertical maior
                  robotModel.position.add(upwardBoost);
                  console.log(`Aplicando impulso vertical extra para ajudar a subir no castelo`);
                }
              } else {
                // Para outras colisões, usar o método normal com um fator de escape suave
                const escapeFactor = 1.1;
                const escapeMove = normal.clone().multiplyScalar(depth * escapeFactor);
                robotModel.position.add(escapeMove);
                console.log(`Escape normal aplicado com fator: ${escapeFactor}`);
              }
            }
          }
        }

        // ABORDAGEM ESTÁTICA: Manter a rotação do robô fixa, sem virar automaticamente
        // O robô só deve virar quando o usuário pressiona explicitamente as teclas de direção

        // Definir a rotação apenas se houver teclas de direção pressionadas
        if (moveState.current.forward || moveState.current.backward ||
            moveState.current.left || moveState.current.right) {

          // Calcular a direção do movimento com base nas teclas pressionadas
          const moveDirection = new Vector3();
          if (moveState.current.forward) moveDirection.z -= 1;
          if (moveState.current.backward) moveDirection.z += 1;
          if (moveState.current.left) moveDirection.x -= 1;
          if (moveState.current.right) moveDirection.x += 1;

          // Só aplicar rotação se houver uma direção clara
          if (moveDirection.length() > 0) {
            moveDirection.normalize();

            // Calcular o ângulo com base na direção do movimento
            let targetRotation = Math.atan2(moveDirection.x, moveDirection.z);

            // Ajustar o ângulo com base na câmera
            targetRotation += moveState.current.cameraAngleX;

            // Aplicar a rotação suavemente (interpolação)
            const currentRotation = robotModel.rotation.y;
            const rotationDiff = targetRotation - currentRotation;

            // Normalizar a diferença para evitar rotações grandes
            let normalizedDiff = rotationDiff;
            while (normalizedDiff > Math.PI) normalizedDiff -= Math.PI * 2;
            while (normalizedDiff < -Math.PI) normalizedDiff += Math.PI * 2;

            // Aplicar uma fração da rotação para suavizar o movimento
            robotModel.rotation.y += normalizedDiff * 0.1;
          }
        }
      }

      // Sistema de debug para validar o movimento direcional durante o pulo
      if (process.env.NODE_ENV === 'development' && moveState.current.isJumping) {
        const horizontalMovement = new Vector3(moveVector.x, 0, moveVector.z);
        const speed = horizontalMovement.length();

        // Verificar se o movimento direcional foi perdido durante o pulo
        if (speed < 0.1 && moveState.current.jumpDirectional.active) {
          console.error("ERRO: Movimento direcional perdido durante o pulo!");
          console.log("Estado atual:", {
            moveVector: moveVector.clone(),
            jumpDirectional: {
              active: moveState.current.jumpDirectional.active,
              direction: moveState.current.jumpDirectional.direction.clone(),
              speed: moveState.current.jumpDirectional.speed
            },
            input: {
              forward: moveState.current.forward,
              backward: moveState.current.backward,
              left: moveState.current.left,
              right: moveState.current.right
            }
          });
        }
      }

      // Notificar a mudança de posição se houve movimento
      if (moved && onPositionChange) {
        onPositionChange({
          x: robotModel.position.x,
          y: robotModel.position.y,
          z: robotModel.position.z
        }, {
          x: robotModel.rotation.x,
          y: robotModel.rotation.y,
          z: robotModel.rotation.z
        });
      }

      // Atualizar a câmera
      if (camera) {
        // Smooth Zoom Aprimorado: Implementação mais sofisticada com easing adaptativo
        // Aplicar inércia à velocidade do zoom com decaimento exponencial
        moveState.current.zoomVelocity *= moveState.current.zoomInertia;

        // Calcular a diferença entre a distância atual e a distância alvo
        const distanceDiff = moveState.current.targetCameraDistance - moveState.current.cameraDistance;
        const absDiff = Math.abs(distanceDiff);

        // Easing adaptativo - mais suave para pequenas mudanças, mais rápido para grandes mudanças
        // Isso cria uma sensação mais natural de movimento
        let adaptiveSmoothingFactor;

        if (absDiff < 1.0) {
          // Para diferenças muito pequenas, usar um fator de suavização muito baixo
          // Isso evita oscilações no final do movimento
          adaptiveSmoothingFactor = moveState.current.zoomSmoothingFactor * 0.5;
        } else if (absDiff > 20.0) {
          // Para diferenças muito grandes, usar um fator de suavização maior
          // Isso permite que a câmera se mova mais rapidamente para grandes distâncias
          adaptiveSmoothingFactor = moveState.current.zoomSmoothingFactor * 1.5;
        } else {
          // Para diferenças médias, usar o fator de suavização padrão
          adaptiveSmoothingFactor = moveState.current.zoomSmoothingFactor;
        }

        // Aplicar easing cúbico para criar uma curva de aceleração/desaceleração mais natural
        // Combinar com a velocidade atual para criar um efeito de inércia
        const smoothingStep = distanceDiff * adaptiveSmoothingFactor + moveState.current.zoomVelocity;

        // Atualizar a distância da câmera
        moveState.current.cameraDistance += smoothingStep;

        // Garantir que a distância esteja dentro dos limites
        moveState.current.cameraDistance = Math.max(
          moveState.current.minZoom,
          Math.min(moveState.current.maxZoom, moveState.current.cameraDistance)
        );

        // Calcular a posição da câmera com base na distância atualizada
        const cameraX = robotModel.position.x + Math.sin(moveState.current.cameraAngleX) * Math.cos(moveState.current.cameraAngleY) * moveState.current.cameraDistance;
        const cameraY = robotModel.position.y + Math.sin(moveState.current.cameraAngleY) * moveState.current.cameraDistance;
        const cameraZ = robotModel.position.z + Math.cos(moveState.current.cameraAngleX) * Math.cos(moveState.current.cameraAngleY) * moveState.current.cameraDistance;

        camera.position.set(cameraX, cameraY, cameraZ);
        camera.lookAt(robotModel.position);
      }
    }
  });

  // Setup keyboard and mouse controls - usando a mesma lógica da spaceship
  useEffect(() => {
    // Skip para jogadores remotos
    if (isRemotePlayer) return;

    // Função para resetar todos os estados de movimento
    const resetAllMovement = () => {
      moveState.current.forward = false;
      moveState.current.backward = false;
      moveState.current.left = false;
      moveState.current.right = false;
      moveState.current.up = false;
      moveState.current.down = false;
      moveState.current.isShiftPressed = false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // NÃO ignoramos eventos repetidos para permitir movimento contínuo
      // Removido: if (e.repeat) return;

      switch(e.key.toLowerCase()) {
        case 'w':
          moveState.current.forward = true;
          if (!moveState.current.isEmoting) {
            moveState.current.currentState = moveState.current.isShiftPressed ? 'Running' : 'Walking';
            actions[moveState.current.currentState]?.play();
          }
          break;
        case 's':
          moveState.current.backward = true;
          if (!moveState.current.isEmoting) {
            moveState.current.currentState = 'Walking';
            actions[moveState.current.currentState]?.play();
          }
          break;
        case 'a':
          moveState.current.left = true;
          if (!moveState.current.isEmoting) {
            moveState.current.currentState = moveState.current.isShiftPressed ? 'Running' : 'Walking';
            actions[moveState.current.currentState]?.play();
          }
          break;
        case 'd':
          moveState.current.right = true;
          if (!moveState.current.isEmoting) {
            moveState.current.currentState = moveState.current.isShiftPressed ? 'Running' : 'Walking';
            actions[moveState.current.currentState]?.play();
          }
          break;
        case ' ': // Pulo com trajetória parabólica física
          if (!moveState.current.isEmoting && !moveState.current.isJumping) {
            // Resetar a animação atual
            Object.values(actions).forEach(action => action.stop());

            // Iniciar animação de pulo
            const jumpAction = actions['Jump'];
            if (jumpAction) {
              jumpAction.reset();
              jumpAction.setLoop(2200, 1); // Não repetir
              jumpAction.timeScale = 1.5; // Acelerar a animação
              jumpAction.play();
            }

            // Implementar pulo físico com trajetória parabólica
            if (robotModel) {
              // Marcar que está pulando
              moveState.current.isJumping = true;

              // Salvar a posição inicial do pulo
              moveState.current.jumpStartPosition.copy(robotModel.position);

              // Definir a velocidade vertical inicial do pulo (impulso para cima)
              // Reduzida para diminuir a distância do pulo pela metade
              moveState.current.jumpVerticalVelocity = 1.0;

              // REDESENHO COMPLETO: Garantir que o pulo SEMPRE siga o movimento direcional
              // Mesmo que não haja teclas de direção pressionadas no momento do pulo,
              // usamos a última direção conhecida ou a direção da câmera

              // 1. Verificar se há teclas de direção pressionadas (prioridade máxima)
              const hasDirectionalInput = moveState.current.forward || moveState.current.backward ||
                                         moveState.current.left || moveState.current.right;

              // 2. Criar o vetor de direção com base no input atual
              const directionVector = new Vector3();
              if (moveState.current.forward) directionVector.z -= 1;
              if (moveState.current.backward) directionVector.z += 1;
              if (moveState.current.left) directionVector.x -= 1;
              if (moveState.current.right) directionVector.x += 1;

              // 3. Se não houver input direcional atual, usar a direção da câmera
              if (directionVector.length() === 0) {
                // Usar a direção para onde a câmera está olhando
                directionVector.z = -1; // Sempre para frente na direção da câmera

                if (process.env.NODE_ENV === 'development') {
                  console.log('Pulo sem input direcional: usando direção da câmera');
                }
              }

              // 4. Normalizar e aplicar rotação da câmera
              directionVector.normalize();
              directionVector.applyAxisAngle(new Vector3(0, 1, 0), moveState.current.cameraAngleX);

              // 5. Calcular a velocidade do pulo
              const jumpSpeed = moveState.current.isShiftPressed ? 1.8 : 1.5;

              // 6. Armazenar o estado direcional explicitamente
              moveState.current.jumpDirectional = {
                active: true, // Sempre ativo, mesmo sem input direcional
                direction: directionVector.clone(),
                speed: jumpSpeed,
                timestamp: performance.now()
              };

              // 7. Definir velocidade inicial do pulo com base na direção
              moveState.current.jumpVelocity.set(
                directionVector.x * jumpSpeed,
                0,
                directionVector.z * jumpSpeed
              );

              if (process.env.NODE_ENV === 'development') {
                console.log('Pulo direcional iniciado:', {
                  direction: directionVector,
                  speed: jumpSpeed,
                  hasDirectionalInput: hasDirectionalInput
                });
              }

              // Não definimos um timeout para terminar o pulo
              // O pulo terminará naturalmente quando o robô atingir o chão
            }
          }
          break;
        case 'control': moveState.current.down = true; break;
        case 'shift':
          moveState.current.isShiftPressed = true;
          // Sempre muda para corrida quando Shift está pressionado e há movimento
          if (moveState.current.forward || moveState.current.backward || moveState.current.left || moveState.current.right) {
            // Força a animação de corrida (Running) quando Shift está pressionado
            moveState.current.currentState = 'Running';

            // Para todos os outros estados de animação e força Running
            Object.values(actions).forEach(action => {
              if (action) action.stop();
            });

            // Inicia a animação de corrida
            actions['Running']?.play();
          }
          break;
        case 'r': // Reset position
          if (robotModel) {
            // Usar a posição inicial armazenada em userData
            if (robotModel.userData.initialPosition) {
              robotModel.position.set(...robotModel.userData.initialPosition);
            } else {
              // Fallback para a posição passada como prop
              robotModel.position.set(...position);
            }
            robotModel.rotation.set(0, 0, 0);

            // Resetar o estado de pulo
            moveState.current.isJumping = false;

            // Voltar para a animação Idle
            if (moveState.current.currentState !== 'Idle') {
              moveState.current.currentState = 'Idle';
              actions['Idle']?.reset();
              actions['Idle']?.play();
            }

            if (process.env.NODE_ENV === 'development') {
              console.log('Robot reposicionado para a posição inicial');
            }
          }
          break;
        // Teclas para emotes
        case '1': actions['Wave']?.play(); break;
        case '2': actions['Yes']?.play(); break;
        case '3': actions['No']?.play(); break;
        case '4': actions['Punch']?.play(); break;
        case '5': actions['ThumbsUp']?.play(); break;
        case '6': actions['Dance']?.play(); break;
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      switch(e.key.toLowerCase()) {
        case 'w': moveState.current.forward = false; break;
        case 's': moveState.current.backward = false; break;
        case 'a': moveState.current.left = false; break;
        case 'd': moveState.current.right = false; break;
        case ' ': /* Não fazemos nada, pois o pulo termina naturalmente quando o robô atinge o chão */ break;
        case 'control': moveState.current.down = false; break;
        case 'shift':
          moveState.current.isShiftPressed = false;

          // Voltar para a animação de caminhada quando soltar o Shift (se estiver se movendo)
          if (moveState.current.forward || moveState.current.backward || moveState.current.left || moveState.current.right) {
            moveState.current.currentState = 'Walking';
            actions[moveState.current.currentState]?.play();
          }
          break;
      }

      // Atualizar animação para Idle quando parar de se mover
      if (!moveState.current.isEmoting &&
          !moveState.current.forward &&
          !moveState.current.backward &&
          !moveState.current.left &&
          !moveState.current.right) {
        moveState.current.currentState = 'Idle';
        actions[moveState.current.currentState]?.play();
      }
    }

    // Atualizar para estado de idle quando não estiver se movendo
    const updateIdleState = () => {
      if (!moveState.current.isEmoting &&
          !moveState.current.forward &&
          !moveState.current.backward &&
          !moveState.current.left &&
          !moveState.current.right) {
        moveState.current.currentState = 'Idle';
        actions[moveState.current.currentState]?.play();
      }
    }

    // Resetar movimento quando a janela perde o foco
    const handleBlur = () => {
      resetAllMovement();
      updateIdleState();
    };

    const handleMouseDown = (e: MouseEvent) => {
      moveState.current.isDragging = true
      moveState.current.lastMouseX = e.clientX
      moveState.current.lastMouseY = e.clientY
    }

    const handleMouseUp = () => {
      moveState.current.isDragging = false
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (moveState.current.isDragging) {
        const deltaX = e.clientX - moveState.current.lastMouseX
        const deltaY = e.clientY - moveState.current.lastMouseY

        // Invertemos o sinal do deltaX para corrigir o movimento invertido
        // (quando move para esquerda, a câmera gira para direita e vice-versa)
        moveState.current.cameraAngleX -= deltaX * 0.01

        // Removemos as limitações do ângulo vertical para permitir rotação completa de 360 graus
        moveState.current.cameraAngleY += deltaY * 0.01

        // Normalizamos o ângulo para mantê-lo entre 0 e 2*PI
        moveState.current.cameraAngleY = moveState.current.cameraAngleY % (Math.PI * 2)
        if (moveState.current.cameraAngleY < 0) moveState.current.cameraAngleY += Math.PI * 2

        moveState.current.lastMouseX = e.clientX
        moveState.current.lastMouseY = e.clientY
      }
    }

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()

      // Variable Zoom: Ajusta a velocidade do zoom com base na distância atual
      // Zoom mais rápido quando distante, mais lento quando próximo
      const currentDistance = moveState.current.cameraDistance
      const distanceRange = moveState.current.maxZoom - moveState.current.minZoom
      const distanceRatio = (currentDistance - moveState.current.minZoom) / distanceRange

      // Função logarítmica aprimorada para velocidade de zoom variável
      // Zoom extremamente lento quando muito próximo (5% da velocidade normal)
      // Zoom normal em distâncias médias
      // Zoom muito rápido quando muito distante (até 400% da velocidade normal)
      const baseZoomSpeed = 2.5 // Aumentado significativamente para zoom muito mais rápido em geral

      // Função de velocidade não-linear mais sofisticada
      // Usa uma curva sigmoide modificada para transições mais suaves entre extremos
      let variableZoomSpeed;

      if (distanceRatio < 0.05) {
        // Zoom extremamente próximo - velocidade muito reduzida (3-10%)
        variableZoomSpeed = baseZoomSpeed * (0.03 + distanceRatio);
      } else if (distanceRatio > 0.85) {
        // Zoom muito distante - velocidade aumentada (300-400%)
        variableZoomSpeed = baseZoomSpeed * (3.0 + (distanceRatio - 0.85) * 7);
      } else {
        // Zona média - velocidade variando suavemente
        variableZoomSpeed = baseZoomSpeed * (0.4 + 2.5 * Math.pow(distanceRatio, 1.3));
      }

      // Direção do zoom (positivo = afastar, negativo = aproximar)
      const zoomDirection = Math.sign(e.deltaY)

      // Calcular o delta de zoom com velocidade variável
      const delta = e.deltaY * 0.01 * variableZoomSpeed

      // Atualizar a velocidade do zoom para inércia
      moveState.current.zoomVelocity += delta * 1.2 // Aumentado para resposta mais rápida

      // Atualizar o alvo da distância da câmera com limites
      moveState.current.targetCameraDistance = Math.max(
        moveState.current.minZoom,
        Math.min(
          moveState.current.maxZoom,
          moveState.current.targetCameraDistance + delta
        )
      )

      // Log para debug
      console.log(`Robot Zoom: distance=${currentDistance.toFixed(2)}, ratio=${distanceRatio.toFixed(2)}, speed=${variableZoomSpeed.toFixed(2)}, delta=${delta.toFixed(2)}`)
    }

    // Adicionar evento para teleportar o robô
    const handleTeleport = (e: CustomEvent) => {
      const { x, y, z } = e.detail;
      console.log(`Teleportando robô para: ${x}, ${y}, ${z}`);

      // Teleportar o robô para a posição especificada
      robotModel.position.set(x, y, z);

      // Resetar o estado de movimento
      moveState.current.isJumping = false;
      moveState.current.jumpVelocity.set(0, 0, 0);
      moveState.current.jumpVerticalVelocity = 0;

      // Voltar para a animação Idle
      if (moveState.current.currentState !== 'Idle') {
        moveState.current.currentState = 'Idle';
        actions['Idle']?.reset();
        actions['Idle']?.play();
      }
    };

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    window.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('wheel', handleWheel, { passive: false })
    window.addEventListener('blur', handleBlur)
    window.addEventListener('teleportPlayer' as any, handleTeleport as EventListener)
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        resetAllMovement();
        updateIdleState();
      }
    })

    // Resetar movimento ao montar o componente
    resetAllMovement();

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      window.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('wheel', handleWheel)
      window.removeEventListener('blur', handleBlur)
      window.removeEventListener('teleportPlayer' as any, handleTeleport as EventListener)
      window.removeEventListener('visibilitychange', () => {})

      // Garantir que os estados de movimento sejam resetados ao desmontar
      resetAllMovement();
    }
  }, [actions, position, robotModel]);

  // Se não devemos renderizar, retornar null imediatamente
  if (!shouldRender) {
    console.log('Robot não será renderizado devido ao status do Multiverso Pass');
    return null;
  }

  // Caixa de colisão oculta no frontend
  return (
    <>
      <primitive object={robotModel} position={position} rotation={rotation} ref={ref} />
    </>
  );
});

export default Robot;
